import { HttpClient, HttpErrorResponse, HttpHeaders, HttpParams } from '@angular/common/http';
import { Inject, Injectable } from '@angular/core';
import { MatDialog, MatDialogRef } from '@angular/material/dialog';
import { TranslateService } from '@ngx-translate/core';
import {
  BasePersona,
  DigitalAgentPersona,
  IAMApiService,
  IAMListPersonasRequestInterface,
  MultiValueAttribute,
  PartnerPersona,
  Persona,
  PersonaType,
  SalespersonPersona,
  User,
  UserInterface,
  fromPersona,
} from '@vendasta/iam';
import { IAMService, UserIdentifier } from '@vendasta/iamv2';
import { PermissionsApiService, ResourceScope, UpdatePermissionsRequestInterface } from '@vendasta/platform-users';
import { ProductAnalyticsService } from '@vendasta/product-analytics';
import { FormResult, VFormTranslationService } from '@vendasta/vform';
import { BehaviorSubject, Observable, combineLatest, of, throwError, ReplaySubject } from 'rxjs';
import { catchError, map, shareReplay, switchMap, take, tap } from 'rxjs/operators';
import { ConfirmationDialogComponent } from '../common/confirmation-dialog.component';
import { DigitalAgent, DigitalAgentInterface } from '../concierge/users/DigitalAgent';
import { ConciergeUserService } from '../concierge/users/concierge-users.service';
import { ManageSalespeopleService } from '../sales/salespeople/manage-salespeople/manage-salespeople.service';
import { GetPermissionsResponseInterface } from '@vendasta/platform-users';
import { NamespaceInterface } from '@vendasta/platform-users/lib/_internal/interfaces/permissions.interface';
import { FeatureFlagsApiService } from '@vendasta/partner';

export interface PartnerAdminConfiguration {
  marketIds?: string[];
  sendEmail?: boolean;
  canAccessDashboard?: boolean;
  canAccessBilling?: boolean;
  canCustomizeWhitelabel?: boolean;
  canAccessCompanyProfile?: boolean;
  canAccessAutomations?: boolean;
  canAccessMarketplace?: boolean;
  canEnableApps?: boolean;
  canAccessAccounts?: boolean;
  canAccessMarketing?: boolean;
  canAccessSales?: boolean;
  canAccessConcierge?: boolean;
  canAccessBrands?: boolean;
  createManageAdminsFlag?: boolean;
  canAccessOrders?: boolean;
  canAccessRetailBilling?: boolean;
  defaultReportAccessScope?: string;
}

export interface PlatformPermissions {
  canWriteCrmContacts?: boolean;
  canWriteCrmActivities?: boolean;
  canWriteCrmCompanies?: boolean;
}

export interface SalespersonConfiguration {
  marketId?: string;
  sendEmail?: boolean;
  isSalesManager?: boolean;
}

export interface TaskManagerConfiguration {
  isManager?: boolean;
  sendEmail?: boolean;
}

export interface TeamMemberConfiguration {
  userId?: string;
  firstName?: string;
  lastName?: string;
  email?: string;
  hasPartnerAdmin?: boolean;
  partnerAdminConfiguration?: PartnerAdminConfiguration;
  platformPermissions?: PlatformPermissions;
  hasSalesperson?: boolean;
  salespersonConfiguration?: SalespersonConfiguration;
  hasTaskManager?: boolean;
  taskManagerConfiguration?: TaskManagerConfiguration;
}

export interface CreateTeamMemberResponse {
  success: Observable<boolean>;
  failedRoles: CreateTeamFailedRoles[];
}

export interface CreateTeamFailedRoles {
  persona: PersonaType;
  error: any;
}

export const DEFAULT_INVITE_MEMBER: TeamMemberConfiguration = {
  salespersonConfiguration: {
    sendEmail: true,
    isSalesManager: true,
  },
  partnerAdminConfiguration: {
    sendEmail: true,
    canAccessDashboard: true,
    canAccessBilling: true,
    canCustomizeWhitelabel: true,
    canAccessCompanyProfile: true,
    canAccessMarketplace: true,
    canEnableApps: true,
    canAccessAccounts: true,
    canAccessMarketing: true,
    canAccessSales: true,
    canAccessConcierge: true,
    canAccessBrands: true,
    createManageAdminsFlag: true,
    canAccessOrders: true,
    canAccessAutomations: true,
    canAccessRetailBilling: true,
    defaultReportAccessScope: 'view',
  },
  platformPermissions: {
    canWriteCrmContacts: true,
    canWriteCrmActivities: true,
    canWriteCrmCompanies: true,
  },
  taskManagerConfiguration: { sendEmail: true },
};

const CREATE_PARTNER_ADMIN_URL = '/form/v1/admin-management/user/create';
const CREATE_SALES_PERSON_URL = '/form/v1/salesPerson/create/';
const EDIT_PARTNER_ADMIN_URL = '/form/v1/admin-management/user/edit';
const EDIT_SALES_PERSON_URL = '/form/v1/salesPerson/edit/';

@Injectable()
export class TeamMemberService {
  private deleteAdminUrl = '/manage-admins/user/delete/';

  private selectedUser$$ = new BehaviorSubject<UserInterface>({});
  selectedUser$ = this.selectedUser$$.asObservable();
  private createAdminError: HttpErrorResponse;
  private createSalespersonError: HttpErrorResponse;
  private createDigitalAgentError: HttpErrorResponse;
  private crmFeatureEnabled$: Observable<boolean>;

  constructor(
    private readonly manageSalespeopleService: ManageSalespeopleService,
    private readonly conciergeUserService: ConciergeUserService,
    private readonly iamApiService: IAMApiService,
    private readonly vFormTranslationService: VFormTranslationService,
    private readonly featureFlagService: FeatureFlagsApiService,
    public matDialog: MatDialog,
    public translateService: TranslateService,
    private http: HttpClient,
    @Inject('PARTNER_ID') public readonly partnerId$: Observable<string>,
    public iamService: IAMService,
    private analyticsService: ProductAnalyticsService,
    private readonly platformPermissionsService: PermissionsApiService,
  ) {
    this.crmFeatureEnabled$ = combineLatest([partnerId$]).pipe(
      map(([partnerId]) => partnerId),
      switchMap((partnerId) => {
        return this.featureFlagService.batchGetStatus({
          featureIds: ['crm_market_restriction'],
          partnerId: partnerId || '',
        });
      }),
      map((response) => {
        return response?.hasAccess.length > 0 && response?.hasAccess[0];
      }),
      shareReplay(1),
    );
  }

  setSelectedUser(user: UserInterface): void {
    this.selectedUser$$.next(user);
  }

  createTeamMember(config: TeamMemberConfiguration): CreateTeamMemberResponse {
    const calls: Observable<boolean>[] = [];
    const failedRoles: CreateTeamFailedRoles[] = [];
    const userCreated$: ReplaySubject<boolean> = new ReplaySubject<boolean>();
    config.email = config.email?.toLowerCase();

    if (config.hasPartnerAdmin) {
      const partnerAdmin$ = this.createPartnerAdmin(config).pipe(
        map((isSuccess) => {
          if (!isSuccess) {
            failedRoles.push({ persona: PersonaType.partner, error: this.createAdminError });
          }
          return isSuccess;
        }),
        tap((s) => {
          userCreated$.next(s);
        }),
      );

      calls.push(partnerAdmin$);
    }
    if (config.hasSalesperson) {
      const salesPerson$ = this.createSalesperson(config).pipe(
        map((isSuccess) => {
          if (!isSuccess) {
            failedRoles.push({ persona: PersonaType.sales_person, error: this.createSalespersonError });
          }
          return isSuccess;
        }),
        tap((s) => {
          userCreated$.next(s);
        }),
      );
      calls.push(salesPerson$);
    }
    if (config.hasTaskManager) {
      this.analyticsService.trackEvent('invite-team-member-digital-agent', 'tm', 'invite-team-member');
      const taskManagerUser$ = this.createTaskManager(config).pipe(
        map((isSuccess) => {
          if (!isSuccess) {
            failedRoles.push({ persona: PersonaType.digital_agent, error: this.createDigitalAgentError });
          }
          return isSuccess;
        }),
      );
      calls.push(taskManagerUser$);
    }
    if (config.hasPartnerAdmin || config.hasSalesperson) {
      const createPermissions$ = combineLatest([this.partnerId$, userCreated$]).pipe(
        map(([p, u]) => {
          if (!u) throw 'User not created';
          else return p;
        }),
        switchMap((partnerId) => {
          // list just created user to get userId to create platform permissions, listUsers will be up-to-date because IAM lists using spanner
          return this.iamApiService.listUsers({ namespace: partnerId, email: config.email, cursor: '', pageSize: 1 });
        }),
        map((res) => res.users[0].userId),
        switchMap((userId: string) => {
          config.userId = userId;
          return this.updatePlatformPermissions(config);
        }),
        catchError(() => of(false)),
      );
      calls.push(createPermissions$);
    }

    const success = combineLatest(calls).pipe(map((resp) => resp.every((x) => x)));
    return { success: success, failedRoles: failedRoles };
  }

  deleteMember(partnerId: string, userId: string, roles: string[]): Observable<boolean> {
    const teamMemberRoles = roles.filter(
      (role) =>
        role === PersonaType.partner.valueOf() ||
        role === PersonaType.sales_person.valueOf() ||
        role === PersonaType.digital_agent.valueOf(),
    );

    if (teamMemberRoles.length === roles.length) {
      return this.deleteRoles(partnerId, userId, roles, true);
    } else {
      return this.deleteRoles(partnerId, userId, roles, false);
    }
  }

  deleteRoles(partnerId: string, userId: string, roles: string[], deleteUser: boolean): Observable<boolean> {
    const personas = this.getUserPersonas(partnerId, userId);

    let partnerDelete: Observable<boolean> = of(true);
    if (roles.includes(PersonaType.partner.valueOf())) {
      partnerDelete = this.deletePartnerAdmin(personas);
    }

    let salesDelete: Observable<boolean> = of(true);
    if (roles.includes(PersonaType.sales_person.valueOf())) {
      salesDelete = this.deleteSalesperson(personas);
    }

    let taskManagerDelete: Observable<boolean> = of(true);
    if (roles.includes(PersonaType.digital_agent.valueOf())) {
      taskManagerDelete = this.deleteTaskManager(personas);
    }

    return combineLatest([partnerDelete, salesDelete, taskManagerDelete]).pipe(
      map(([pd, sd, td]) => {
        return pd && sd && td;
      }),
      switchMap((success) => {
        if (success && deleteUser) {
          return this.deleteUser(userId);
        }
        return of(success);
      }),
    );
  }

  deleteUser(userId: string): Observable<boolean> {
    return this.iamService.deleteUser(new UserIdentifier({ userId: userId })).pipe(
      map(() => {
        return true;
      }),
      catchError(() => of(false)),
    );
  }

  editMember(
    partnerId: string,
    current: TeamMemberConfiguration,
    update: TeamMemberConfiguration,
  ): Observable<boolean> {
    const personas$ = this.getUserPersonas(partnerId, update.userId);
    const calls: Observable<boolean>[] = [];
    this.createAdminError = undefined;
    this.createSalespersonError = undefined;
    this.createDigitalAgentError = undefined;

    if (this.partnerAdminUpdated(update, current)) {
      calls.push(this.editPartnerAdmin(update, personas$));
    } else if (update.hasPartnerAdmin && !current.hasPartnerAdmin) {
      calls.push(this.createPartnerAdmin(update));
    } else if (!update.hasPartnerAdmin && current.hasPartnerAdmin) {
      calls.push(this.deletePartnerAdmin(personas$));
    }

    if (this.salesPersonUpdated(update, current)) {
      calls.push(this.editSalesperson(update, personas$));
    } else if (update.hasSalesperson && !current.hasSalesperson) {
      calls.push(this.createSalesperson(update));
    } else if (!update.hasSalesperson && current.hasSalesperson) {
      calls.push(this.deleteSalesperson(personas$));
    }

    if (this.taskManagerUserUpdated(current, update)) {
      calls.push(this.editTaskManager(update, personas$));
    } else if (update.hasTaskManager && !current.hasTaskManager) {
      calls.push(this.createTaskManager(update));
    } else if (!update.hasTaskManager && current.hasTaskManager) {
      calls.push(this.deleteTaskManager(personas$));
    }

    if (this.platformPermissionsUpdated(current, update)) {
      calls.push(this.updatePlatformPermissions(update));
    }

    return combineLatest(calls).pipe(map((resp) => resp.every((x) => x)));
  }

  getCreateTeamFailedRoles(): CreateTeamFailedRoles[] {
    const personas: CreateTeamFailedRoles[] = [];
    if (this.createAdminError) {
      personas.push(<CreateTeamFailedRoles>{
        persona: PersonaType.partner,
        error: this.createAdminError,
      });
    }
    if (this.createSalespersonError) {
      personas.push(<CreateTeamFailedRoles>{
        persona: PersonaType.sales_person,
        error: this.createSalespersonError,
      });
    }
    if (this.createDigitalAgentError) {
      personas.push(<CreateTeamFailedRoles>{
        persona: PersonaType.digital_agent,
        error: this.createDigitalAgentError,
      });
    }
    return personas;
  }

  private partnerAdminUpdated(updatedMember: TeamMemberConfiguration, currentMember: TeamMemberConfiguration): boolean {
    return (
      updatedMember.hasPartnerAdmin &&
      currentMember.hasPartnerAdmin &&
      (this.partnerAdminPermissionsUpdated(updatedMember, currentMember) ||
        !this.commonConfigIsEqual(currentMember, updatedMember) ||
        !this.partnerAdminConfigIsEqual(currentMember, updatedMember))
    );
  }

  private salesPersonUpdated(updatedMember: TeamMemberConfiguration, currentMember: TeamMemberConfiguration): boolean {
    return (
      updatedMember.hasSalesperson &&
      currentMember.hasSalesperson &&
      (!this.commonConfigIsEqual(currentMember, updatedMember) ||
        !this.salespersonConfgIsEqual(currentMember, updatedMember))
    );
  }

  private taskManagerUserUpdated(
    updatedMember: TeamMemberConfiguration,
    currentMember: TeamMemberConfiguration,
  ): boolean {
    return (
      updatedMember.hasTaskManager &&
      currentMember.hasTaskManager &&
      (!this.commonConfigIsEqual(currentMember, updatedMember) ||
        !this.taskManagerConfigIsEqual(currentMember, updatedMember))
    );
  }

  private partnerAdminPermissionsUpdated(
    updatedMember: TeamMemberConfiguration,
    currentMember: TeamMemberConfiguration,
  ): boolean {
    return updatedMember.partnerAdminConfiguration !== currentMember.partnerAdminConfiguration;
  }

  private platformPermissionsUpdated(
    updatedMember: TeamMemberConfiguration,
    currentMember: TeamMemberConfiguration,
  ): boolean {
    return (
      currentMember.platformPermissions !== updatedMember.platformPermissions ||
      currentMember.hasPartnerAdmin !== updatedMember.hasPartnerAdmin ||
      currentMember.hasSalesperson !== updatedMember.hasSalesperson ||
      currentMember.partnerAdminConfiguration?.marketIds?.length !==
        updatedMember.partnerAdminConfiguration?.marketIds?.length ||
      (currentMember.partnerAdminConfiguration?.marketIds?.length == 1 &&
        updatedMember.partnerAdminConfiguration?.marketIds?.length == 1 &&
        currentMember.partnerAdminConfiguration?.marketIds[0] !==
          updatedMember.partnerAdminConfiguration?.marketIds[0]) ||
      currentMember.salespersonConfiguration?.marketId !== updatedMember.salespersonConfiguration?.marketId
    );
  }

  private taskManagerConfigIsEqual(current: TeamMemberConfiguration, update: TeamMemberConfiguration): boolean {
    return (
      (current?.taskManagerConfiguration?.isManager || false) ===
        (update?.taskManagerConfiguration?.isManager || false) &&
      (current?.taskManagerConfiguration?.sendEmail || false) === (update?.taskManagerConfiguration?.sendEmail || false)
    );
  }

  private salespersonConfgIsEqual(current: TeamMemberConfiguration, update: TeamMemberConfiguration): boolean {
    return (
      (current?.salespersonConfiguration?.marketId || '') === (update?.salespersonConfiguration?.marketId || '') &&
      (current?.salespersonConfiguration?.isSalesManager || false) ===
        (update?.salespersonConfiguration?.isSalesManager || false)
    );
  }

  private commonConfigIsEqual(current: TeamMemberConfiguration, update: TeamMemberConfiguration): boolean {
    return (
      current.firstName === update.firstName && current.lastName === update.lastName && current.email === update.email
    );
  }

  private partnerAdminConfigIsEqual(current: TeamMemberConfiguration, update: TeamMemberConfiguration): boolean {
    const currentMarkets = current?.partnerAdminConfiguration?.marketIds || [];
    const updatedMarkets = update?.partnerAdminConfiguration?.marketIds || [];
    return currentMarkets.length === updatedMarkets.length && currentMarkets.every((id) => updatedMarkets.includes(id));
  }

  getName(user: TeamMemberConfiguration | User): string {
    let name = `${user.firstName || ''} ${user.lastName || ''}`.trim();
    if (!name) {
      name = user.email;
    }
    return name;
  }

  getPlatformPermissions(partnerId: string, userId: string): Observable<PlatformPermissions> {
    return this.platformPermissionsService
      .getPermissions({
        partnerId: partnerId,
        userId: userId,
      })
      .pipe(
        map((resp) => {
          const partnerInNamespace = !!resp?.permissions?.namespace?.find((ns) => ns.namespaceId === partnerId);
          const canWriteContacts = resp?.permissions?.contacts?.writeScope === ResourceScope.RESOURCE_SCOPE_ALL;
          const canWriteActivities = resp?.permissions?.activities?.writeScope === ResourceScope.RESOURCE_SCOPE_ALL;
          const canWriteCompanies = resp?.permissions?.companies?.writeScope === ResourceScope.RESOURCE_SCOPE_ALL;
          return {
            canWriteCrmContacts: partnerInNamespace && canWriteContacts,
            canWriteCrmActivities: partnerInNamespace && canWriteActivities,
            canWriteCrmCompanies: partnerInNamespace && canWriteCompanies,
          };
        }),
        catchError((err: HttpErrorResponse) => {
          if (err.status === 404) {
            return of({
              canWriteCrmContacts: false,
              canWriteCrmActivities: false,
              canWriteCrmCompanies: false,
            });
          }
          throw err;
        }),
      );
  }

  getTaskManagerConfig(userPersonas$: Observable<BasePersona[]>): Observable<TaskManagerConfiguration> {
    return userPersonas$.pipe(
      switchMap((personas) => {
        const taskManager = this.getDigitalAgentPersona(personas);
        if (taskManager) {
          return this.partnerId$.pipe(
            take(1),
            switchMap((partnerId: string) => {
              return this.conciergeUserService.getUser(partnerId, taskManager.subjectId);
            }),
          );
        }
        return of(null);
      }),
      map((taskManager: DigitalAgentInterface | undefined) => {
        if (!taskManager) {
          return {};
        }
        const partners = Object.keys(taskManager?.partnerPermissions || []);
        const isManager = Boolean(
          partners.find((partnerId) => taskManager.partnerPermissions?.[partnerId].can_delete_tasks),
        );
        return { isManager };
      }),
    );
  }

  getMemberConfiguration(
    partnerId: string,
    userId: string,
    fallbackFirstName?: string,
    fallbackLastName?: string,
    fallbackUserEmail?: string,
  ): Observable<TeamMemberConfiguration | null> {
    if (!userId) {
      return of({});
    }

    const userPersonas$ = this.getUserPersonas(partnerId, userId);
    const taskManagerConfig$ = this.getTaskManagerConfig(userPersonas$);
    const platformPermissions$ = this.getPlatformPermissions(partnerId, userId);

    return combineLatest([userPersonas$, taskManagerConfig$, platformPermissions$]).pipe(
      map(([personas, taskManagerConfig, platformPermissions]) => {
        const tmc: TeamMemberConfiguration = {
          userId: userId,
          partnerAdminConfiguration: {},
          platformPermissions: {},
          salespersonConfiguration: {},
          taskManagerConfiguration: {},
        };
        const partnerAdmin = this.getPartnerPersona(personas);
        if (partnerAdmin) {
          tmc.hasPartnerAdmin = true;
          tmc.partnerAdminConfiguration = {
            marketIds: partnerAdmin.accessibleMarkets,
            canAccessDashboard: partnerAdmin.canAccessDashboard,
            canAccessBilling: partnerAdmin.canAccessBilling,
            canCustomizeWhitelabel: partnerAdmin.canCustomizeWhitelabel,
            canAccessCompanyProfile: partnerAdmin.canAccessCompanyProfile,
            canAccessMarketplace: partnerAdmin.canAccessMarketplace,
            canEnableApps: partnerAdmin.canEnableApps,
            canAccessAccounts: partnerAdmin.canAccessAccounts,
            canAccessMarketing: partnerAdmin.canAccessMarketing,
            canAccessSales: partnerAdmin.canAccessSales,
            canAccessConcierge: partnerAdmin.canAccessConcierge,
            canAccessBrands: partnerAdmin.canAccessBrands,
            createManageAdminsFlag: partnerAdmin.isAdmin,
            canAccessOrders: partnerAdmin.canAccessOrders,
            canAccessAutomations: partnerAdmin.canAccessAutomations,
            canAccessRetailBilling: partnerAdmin.canAccessRetailBilling,
            defaultReportAccessScope: partnerAdmin.defaultReportAccessScope,
          };
          this.applyUserValuesIfNotSet(tmc, partnerAdmin);
        } else {
          tmc.partnerAdminConfiguration = DEFAULT_INVITE_MEMBER.partnerAdminConfiguration;
        }
        const salesperson = this.getSalespersonPersona(personas);
        if (salesperson) {
          tmc.hasSalesperson = true;
          tmc.salespersonConfiguration = {
            marketId: salesperson.marketId,
            isSalesManager: salesperson.isSalesManager,
          };
          this.applyUserValuesIfNotSet(tmc, salesperson);
        } else {
          tmc.salespersonConfiguration = DEFAULT_INVITE_MEMBER.salespersonConfiguration;
        }
        const taskManager = this.getDigitalAgentPersona(personas);
        if (taskManager) {
          tmc.hasTaskManager = true;
          tmc.taskManagerConfiguration = taskManagerConfig;
        } else {
          tmc.taskManagerConfiguration = DEFAULT_INVITE_MEMBER.taskManagerConfiguration;
          this.applyUserValuesIfNotSet(tmc, taskManager);
        }
        this.applyUserValuesIfNotSet(tmc, {
          firstName: fallbackFirstName,
          lastName: fallbackLastName,
          email: fallbackUserEmail,
        });
        if (!partnerAdmin && !salesperson && !taskManager) {
          return null;
        }
        tmc.platformPermissions = platformPermissions;
        return tmc;
      }),
    );
  }

  private applyUserValuesIfNotSet(
    tmc: TeamMemberConfiguration,
    vals: { firstName: string; lastName: string; email: string },
  ): void {
    tmc.firstName = tmc.firstName || vals?.firstName;
    tmc.lastName = tmc.lastName || vals?.lastName;
    tmc.email = tmc.email || vals?.email;
  }

  private createTaskManager(config: TeamMemberConfiguration): Observable<boolean> {
    return this.partnerId$.pipe(
      switchMap((partnerId) => {
        return this.conciergeUserService.createUserWithoutAlert(
          partnerId,
          config.email,
          config.firstName,
          config.lastName,
          config.taskManagerConfiguration.sendEmail || false,
          config.taskManagerConfiguration.isManager || false,
        );
      }),
      catchError((response) => {
        this.createDigitalAgentError = response.error;
        return of(false);
      }),
    );
  }

  private createSalesperson(config: TeamMemberConfiguration): Observable<boolean> {
    return this.vFormTranslationService
      .submitForm(CREATE_SALES_PERSON_URL, this.setDataForSalesPerson(config, undefined).toString(), '', true)
      .pipe(
        map((resp: FormResult) => resp.statusCode === 200),
        catchError((response: HttpErrorResponse) => {
          this.createSalespersonError = response.error;
          return of(false);
        }),
      );
  }

  private createPartnerAdmin(config: TeamMemberConfiguration): Observable<boolean> {
    return this.vFormTranslationService
      .submitForm(CREATE_PARTNER_ADMIN_URL, this.setDataForPartnerAdmin(config).toString(), '', true)
      .pipe(
        map((resp: FormResult) => resp.statusCode === 200),
        catchError((response: HttpErrorResponse) => {
          this.createAdminError = response.error;
          return of(false);
        }),
      );
  }

  private deleteTaskManager(personas: Observable<BasePersona[]>): Observable<boolean> {
    return combineLatest([this.partnerId$, personas]).pipe(
      switchMap(([partnerId, personasInput]) => {
        const iamAgent = this.getDigitalAgentPersona(personasInput);
        if (iamAgent) {
          return this.conciergeUserService
            .getUser(partnerId, iamAgent.subjectId)
            .pipe(switchMap((agent) => this.conciergeUserService.revokeUserWithoutAlert(partnerId, agent)));
        }
        return of(true);
      }),
    );
  }

  private deleteSalesperson(personas: Observable<BasePersona[]>): Observable<boolean> {
    return personas.pipe(
      switchMap((personasInput) => {
        const salesperson = this.getSalespersonPersona(personasInput);
        if (salesperson) {
          return this.manageSalespeopleService.deleteSalesperson(salesperson.subjectId);
        }
        return of('success');
      }),
      map((successStr) => successStr.length > 0),
    );
  }

  private deletePartnerAdmin(personas: Observable<BasePersona[]>): Observable<boolean> {
    return personas.pipe(
      switchMap((personasInput) => {
        const partner = this.getPartnerPersona(personasInput);
        if (partner) {
          return this.deleteAdminUser(partner.email);
        }
        return of('');
      }),
      map((successStr) => successStr.length === 0),
    );
  }

  private deleteAdminUser(email: string): Observable<string> {
    // Need to do it like this to make a python decorator happy 🤢
    const body = new HttpParams().set('email', encodeURIComponent(email));
    return this.http
      .post(this.deleteAdminUrl, body.toString(), {
        headers: new HttpHeaders({ 'Content-Type': 'application/x-www-form-urlencoded; charset=UTF-8' }),
      })
      .pipe(
        map(() => ''),
        catchError((err) => {
          if (err.status === 404) {
            return of(this.translateService.instant('ADMIN.ADMIN_MANAGEMENT.USER_NOT_FOUND'));
          }
          if (err.status === 401) {
            return of(this.translateService.instant('ADMIN.ADMIN_MANAGEMENT.CANT_DELETE_OWN_ACCOUNT'));
          }
          if (err.status === 400) {
            return of(this.translateService.instant('ADMIN.ADMIN_MANAGEMENT.FAILED_TO_DELETE_ACCOUNT'));
          }
          return of(this.translateService.instant('ADMIN.ADMIN_MANAGEMENT.UNKNOWN_ERROR'));
        }),
      );
  }

  private editPartnerAdmin(update: TeamMemberConfiguration, personas$: Observable<BasePersona[]>): Observable<boolean> {
    return personas$.pipe(
      switchMap((personas) => {
        const persona = this.getPartnerPersona(personas);
        if (!persona) {
          return of(false);
        }

        return this.vFormTranslationService
          .submitForm(
            EDIT_PARTNER_ADMIN_URL,
            this.setDataForPartnerAdmin(update, true).toString(),
            encodeURIComponent(`{"user_email":"${update.email}"}`),
            true,
          )
          .pipe(
            map((resp: FormResult) => resp.statusCode === 200),
            catchError((_) => of(false)),
          );
      }),
    );
  }

  private editSalesperson(config: TeamMemberConfiguration, personas$: Observable<BasePersona[]>): Observable<boolean> {
    return personas$.pipe(
      switchMap((personas) => {
        const persona = this.getSalespersonPersona(personas);
        if (!persona) {
          return of(false);
        }

        return this.vFormTranslationService
          .submitForm(
            EDIT_SALES_PERSON_URL,
            this.setDataForSalesPerson(config, persona).toString(),
            escape(`${window.location.origin}/st/edit?sales_person_id=${persona.subjectId}`.toString()),
            true,
          )
          .pipe(
            map((resp: FormResult) => resp.statusCode === 200),
            catchError((_) => of(false)),
          );
      }),
    );
  }

  private editTaskManager(update: TeamMemberConfiguration, personas$: Observable<BasePersona[]>): Observable<boolean> {
    const digitalAgent$: Observable<DigitalAgent> = personas$.pipe(
      switchMap((personas) => {
        const taskManager = this.getDigitalAgentPersona(personas);
        if (taskManager) {
          return this.partnerId$.pipe(
            take(1),
            switchMap((partnerId: string) => {
              return this.conciergeUserService.getUser(partnerId, taskManager.subjectId);
            }),
          );
        }
        return of(null);
      }),
    );

    return combineLatest([this.partnerId$, digitalAgent$]).pipe(
      switchMap(([partnerId, digitalAgent]) => {
        if (!digitalAgent) {
          throwError('Digital Agent not found');
        }

        const user: DigitalAgentInterface = digitalAgent;
        user.firstName = update.firstName;
        user.lastName = update.lastName;
        return this.conciergeUserService.editUser(
          partnerId,
          user,
          update.taskManagerConfiguration.sendEmail,
          update.taskManagerConfiguration.isManager,
        );
      }),
      map(() => true),
      catchError((err) => {
        console.error('Could not edit Task Manager', err);
        return of(false);
      }),
    );
  }

  private getExistingPlatformPermissionsNamespaces(config: TeamMemberConfiguration): Observable<NamespaceInterface[]> {
    return this.partnerId$.pipe(
      switchMap((partnerId) => {
        return this.platformPermissionsService.getPermissions({
          partnerId: partnerId,
          userId: config.userId,
        });
      }),
      catchError(() => {
        return of({ permissions: {} } as GetPermissionsResponseInterface);
      }),
      map((existingPermissions: GetPermissionsResponseInterface) => existingPermissions?.permissions?.namespace || []),
    );
  }

  private buildNamespaces(partnerId: string, existingNamespaces: NamespaceInterface[]): NamespaceInterface[] {
    if (existingNamespaces.find((e) => e.namespaceType === 'partner' && e.namespaceId === partnerId)) {
      return existingNamespaces;
    }
    existingNamespaces.push({ namespaceType: 'partner', namespaceId: partnerId });
    return existingNamespaces;
  }

  private updatePlatformPermissions(config: TeamMemberConfiguration): Observable<boolean> {
    return combineLatest([
      this.partnerId$,
      this.getExistingPlatformPermissionsNamespaces(config),
      this.crmFeatureEnabled$,
    ]).pipe(
      switchMap(([partnerId, existingNamespaces, featureEnabled]) => {
        const request: UpdatePermissionsRequestInterface = {
          permissions: {
            partnerId: partnerId,
            userId: config.userId,
            namespace: this.buildNamespaces(partnerId, existingNamespaces),
          },
        };
        if (config.platformPermissions.canWriteCrmContacts) {
          request.permissions.contacts = {
            readScope: ResourceScope.RESOURCE_SCOPE_ALL,
            writeScope: ResourceScope.RESOURCE_SCOPE_ALL,
          };
        }
        if (config.platformPermissions.canWriteCrmActivities) {
          request.permissions.activities = {
            readScope: ResourceScope.RESOURCE_SCOPE_ALL,
            writeScope: ResourceScope.RESOURCE_SCOPE_ALL,
          };
        }
        if (config.platformPermissions.canWriteCrmCompanies) {
          request.permissions.companies = {
            readScope: ResourceScope.RESOURCE_SCOPE_ALL,
            writeScope: ResourceScope.RESOURCE_SCOPE_ALL,
          };
        }

        request.permissions.restrictedToMarketId = '';
        if (featureEnabled) {
          if (config.hasPartnerAdmin) {
            if (
              config.partnerAdminConfiguration?.marketIds &&
              config.partnerAdminConfiguration?.marketIds?.length == 1 &&
              config.partnerAdminConfiguration.marketIds[0] != '_DEFAULT'
            ) {
              request.permissions.restrictedToMarketId = config.partnerAdminConfiguration.marketIds[0];
            }
          } else if (config.hasSalesperson) {
            request.permissions.restrictedToMarketId = config.salespersonConfiguration?.marketId;
          }
        }

        return this.platformPermissionsService.updatePermissions(request);
      }),
      map(() => true),
      catchError((err) => {
        console.error('Could not update Platform Permissions', err);
        return of(false);
      }),
    );
  }

  public getDigitalAgentPersona(personas: BasePersona[]): DigitalAgentPersona {
    return personas.find((persona) => persona instanceof DigitalAgentPersona) as DigitalAgentPersona;
  }

  public getSalespersonPersona(personas: BasePersona[]): SalespersonPersona {
    return personas.find((persona) => persona instanceof SalespersonPersona) as SalespersonPersona;
  }

  public getPartnerPersona(personas: BasePersona[]): PartnerPersona {
    return personas.find((persona) => persona instanceof PartnerPersona) as PartnerPersona;
  }

  public getUserPersonas(partnerId: string, userId: string): Observable<BasePersona[]> {
    const req: IAMListPersonasRequestInterface = {
      userIdentifier: {
        userId: userId,
      },
      namespace: partnerId,
    };

    return this.iamApiService.listPersonas(req).pipe(
      map((resp) => {
        return (resp.personas || []).map((p) => {
          const persona = this.getPersonaWithRetailBillingAttribute(p);
          return fromPersona(persona);
        });
      }),
      map((ps: any[]) => ps.filter((p) => p.partnerId === partnerId)), // Not required after INF-1398
      shareReplay(1),
    );
  }

  openDeleteConfirmationDialog(
    deleteUser: boolean,
    deletePartnerAdmin: boolean,
    deleteSalesperson: boolean,
    deleteTaskManager: boolean,
    name: string,
  ): MatDialogRef<ConfirmationDialogComponent> {
    let titlePre = 'MANAGE_TEAM.MY_TEAM.ACTIONS.DELETE.DELETE_DIALOG.TITLE.START';
    let title = name;
    let titlePost = 'MANAGE_TEAM.MY_TEAM.ACTIONS.DELETE.DELETE_DIALOG.TITLE.END';
    let message = 'MANAGE_TEAM.MY_TEAM.ACTIONS.DELETE.DELETE_DIALOG.ADMIN_ONLY_MESSAGE_V2';
    let confirm = 'MANAGE_TEAM.MY_TEAM.ACTIONS.DELETE.DELETE_DIALOG.CONFIRM';
    let decline = 'MANAGE_TEAM.MY_TEAM.ACTIONS.DELETE.DELETE_DIALOG.DECLINE';

    if (!deleteUser) {
      titlePre = '';
      titlePost = '';
      const roles: string[] = [];
      if (deletePartnerAdmin) {
        roles.push(this.translateService.instant('MANAGE_TEAM.ROLES.partner.NAME'));
      }
      if (deleteSalesperson) {
        roles.push(this.translateService.instant('MANAGE_TEAM.ROLES.sales_person.NAME'));
      }
      if (deleteTaskManager) {
        roles.push(this.translateService.instant('MANAGE_TEAM.ROLES.digital_agent.NAME'));
      }
      if (roles.length === 1) {
        title = this.translateService.instant('MANAGE_TEAM.MY_TEAM.ACTIONS.REMOVE_ROLE.TITLE_1_ROLE', {
          role1: roles[0],
        });
        confirm = 'MANAGE_TEAM.MY_TEAM.ACTIONS.REMOVE_ROLE.CONFIRM_SINGULAR';
        decline = 'MANAGE_TEAM.MY_TEAM.ACTIONS.REMOVE_ROLE.DECLINE_SINGULAR';
      } else {
        title = this.translateService.instant('MANAGE_TEAM.MY_TEAM.ACTIONS.REMOVE_ROLE.TITLE_2_ROLES', {
          role1: roles[0],
          role2: roles[1],
        });
        confirm = 'MANAGE_TEAM.MY_TEAM.ACTIONS.REMOVE_ROLE.CONFIRM_PLURAL';
        decline = 'MANAGE_TEAM.MY_TEAM.ACTIONS.REMOVE_ROLE.DECLINE_PLURAL';
      }
      const messageKey = 'MANAGE_TEAM.MY_TEAM.ACTIONS.REMOVE_ROLE.ADMIN_ONLY_MESSAGE_V2';
      message = this.translateService.instant(messageKey, { name: name });
    }

    if (deleteSalesperson && !deleteTaskManager) {
      if (deleteUser) {
        message = 'MANAGE_TEAM.MY_TEAM.ACTIONS.DELETE.DELETE_DIALOG.SALES_MESSAGE';
      } else {
        message = this.translateService.instant('MANAGE_TEAM.MY_TEAM.ACTIONS.REMOVE_ROLE.SALES_MESSAGE', {
          name: name,
        });
      }
    }
    if (!deleteSalesperson && deleteTaskManager) {
      if (deleteUser) {
        message = 'MANAGE_TEAM.MY_TEAM.ACTIONS.DELETE.DELETE_DIALOG.TASK_MANAGER_MESSAGE';
      } else {
        message = this.translateService.instant('MANAGE_TEAM.MY_TEAM.ACTIONS.REMOVE_ROLE.TASK_MANAGER_MESSAGE', {
          name: name,
        });
      }
    }
    if (deleteSalesperson && deleteTaskManager) {
      if (deleteUser) {
        message = 'MANAGE_TEAM.MY_TEAM.ACTIONS.DELETE.DELETE_DIALOG.ALL_MESSAGE';
      } else {
        message = this.translateService.instant('MANAGE_TEAM.MY_TEAM.ACTIONS.REMOVE_ROLE.ALL_MESSAGE', {
          name: name,
        });
      }
    }

    return this.matDialog.open(ConfirmationDialogComponent, {
      data: {
        titlePre: titlePre,
        title: title,
        titlePost: titlePost,
        message: message,
        warn: true,
        buttonText: {
          confirm: confirm,
          decline: decline,
        },
      },
    });
  }

  private setDataForPartnerAdmin(config: TeamMemberConfiguration, edit = false): URLSearchParams {
    const formdata = new URLSearchParams();
    if (config.partnerAdminConfiguration.marketIds.length === 0) {
      formdata.append('markets', '_DEFAULT');
    } else {
      config.partnerAdminConfiguration.marketIds.forEach((m) => {
        formdata.append('markets', m);
      });
    }
    formdata.append('firstName', config.firstName);
    formdata.append('lastName', config.lastName);
    formdata.append('user_email', config.email);
    if (!edit) {
      formdata.append('sendWelcomeEmailFlag', JSON.stringify(config.partnerAdminConfiguration?.sendEmail || false));
    }
    formdata.append(
      'accessDashboardFlag',
      JSON.stringify(config.partnerAdminConfiguration?.canAccessDashboard || false),
    );
    formdata.append(
      'accessBillingReportsFlag',
      JSON.stringify(config.partnerAdminConfiguration?.canAccessBilling || false),
    );
    formdata.append(
      'customizeWhitelabelFlag',
      JSON.stringify(config.partnerAdminConfiguration?.canCustomizeWhitelabel || false),
    );
    formdata.append(
      'accessCompanyProfileFlag',
      JSON.stringify(config.partnerAdminConfiguration?.canAccessCompanyProfile || false),
    );
    formdata.append(
      'accessMarketplaceFlag',
      JSON.stringify(config.partnerAdminConfiguration?.canAccessMarketplace || false),
    );
    formdata.append('enableAppFlag', JSON.stringify(config.partnerAdminConfiguration?.canEnableApps || false));
    formdata.append('accessAccountsFlag', JSON.stringify(config.partnerAdminConfiguration?.canAccessAccounts || false));
    formdata.append(
      'accessMarketingFlag',
      JSON.stringify(config.partnerAdminConfiguration?.canAccessMarketing || false),
    );
    formdata.append('accessSalesFlag', JSON.stringify(config.partnerAdminConfiguration?.canAccessSales || false));
    formdata.append(
      'accessConciergeFlag',
      JSON.stringify(config.partnerAdminConfiguration?.canAccessConcierge || false),
    );
    formdata.append('accessBrandsFlag', JSON.stringify(config.partnerAdminConfiguration?.canAccessBrands || false));
    formdata.append(
      'createManageAdminsFlag',
      JSON.stringify(config.partnerAdminConfiguration?.createManageAdminsFlag || false),
    );
    formdata.append('accessOrdersFlag', JSON.stringify(config.partnerAdminConfiguration?.canAccessOrders || false));
    formdata.append(
      'accessAutomationsFlag',
      JSON.stringify(config.partnerAdminConfiguration?.canAccessAutomations || false),
    );
    formdata.append(
      'accessRetailBillingFlag',
      JSON.stringify(config.partnerAdminConfiguration?.canAccessRetailBilling || false),
    );
    formdata.append(
      'defaultReportAccessScope',
      config.partnerAdminConfiguration.defaultReportAccessScope || 'noAccess',
    );
    return formdata;
  }

  private setDataForSalesPerson(config: TeamMemberConfiguration, persona: SalespersonPersona): URLSearchParams {
    const formdata = new URLSearchParams();
    formdata.append(
      'marketId',
      config.salespersonConfiguration.marketId ? config.salespersonConfiguration.marketId : 'default',
    );
    formdata.append('firstName', config.firstName);
    formdata.append('lastName', config.lastName);
    formdata.append('email', config.email);
    if (!persona) {
      formdata.append('sendWelcomeEmailFlag', JSON.stringify(config.salespersonConfiguration?.sendEmail || false));
    }
    formdata.append('isSalesManagerFlag', JSON.stringify(config.salespersonConfiguration?.isSalesManager || false));
    formdata.append('coverPageTitle', persona ? persona.titleChoice.valueOf() : 'partnerForSmb');
    return formdata;
  }

  getPersonaWithRetailBillingAttribute(persona: Persona): Persona {
    if (
      !persona.subject?.structAttributes?.attributes?.can_access_retail_billing &&
      !!persona.subject?.structAttributes?.attributes?.can_access_billing
    ) {
      persona.subject.structAttributes.attributes.can_access_retail_billing =
        persona.subject.structAttributes.attributes.can_access_billing;
      let dupe = false;
      let retailBillingAttribute: MultiValueAttribute;
      for (const attribute of persona.subject.attributes) {
        if (attribute.key === 'can_access_retail_billing') {
          dupe = true;
        }
        if (attribute.key === 'can_access_billing') {
          retailBillingAttribute = new MultiValueAttribute();
          retailBillingAttribute.key = 'can_access_retail_billing';
          retailBillingAttribute.values = attribute.values;
        }
      }
      if (!dupe && !!retailBillingAttribute) {
        persona.subject.attributes.push(retailBillingAttribute);
      }
    }
    return persona;
  }
}
